import React, { useEffect, useState } from 'react';
import { ArrowRight, CheckCircle2, HelpCircle, XIcon, CalendarClock, Target, Users, DollarSign } from 'lucide-react';
import { Button } from './Button';
interface JoinPilotModalProps {
  isOpen: boolean;
  onClose: () => void;
  origin: {
    x: number;
    y: number;
  } | null;
}
interface FormData {
  fullName: string;
  email: string;
  city: string;
  state: string;
  schools: string;
  coachingCapacity: {
    schoolCoach: boolean;
    districtCoach: boolean;
    coachingProviderEmployee: boolean;
    contractor: boolean;
    administrator: boolean;
    other: boolean;
    otherText: string;
  };
  subjectsAndGrades: string;
  curricula: {
    illustrativeMath: boolean;
    eurekaMath: boolean;
    other: boolean;
    otherText: string;
  };
  teachingLabRelationship: {
    fullTimeEmployee: boolean;
    partTimeFacilitator: boolean;
    tabMember: boolean;
    notAffiliated: boolean;
  };
  hearAboutNisa: string;
  whyInterested: string;
}
export const JoinPilotModal: React.FC<JoinPilotModalProps> = ({
  isOpen,
  onClose,
  origin
}) => {
  const [animationComplete, setAnimationComplete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    city: '',
    state: '',
    schools: '',
    coachingCapacity: {
      schoolCoach: false,
      districtCoach: false,
      coachingProviderEmployee: false,
      contractor: false,
      administrator: false,
      other: false,
      otherText: ''
    },
    subjectsAndGrades: '',
    curricula: {
      illustrativeMath: false,
      eurekaMath: false,
      other: false,
      otherText: ''
    },
    teachingLabRelationship: {
      fullTimeEmployee: false,
      partTimeFacilitator: false,
      tabMember: false,
      notAffiliated: false
    },
    hearAboutNisa: '',
    whyInterested: ''
  });
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    if (type === 'checkbox') {
      const checkbox = e.target as HTMLInputElement;
      const checked = checkbox.checked;
      if (name.startsWith('curricula.')) {
        const curriculaField = name.split('.')[1];
        setFormData(prev => ({
          ...prev,
          curricula: {
            ...prev.curricula,
            [curriculaField]: checked
          }
        }));
      } else if (name.startsWith('teachingLabRelationship.')) {
        const relationshipField = name.split('.')[1];
        setFormData(prev => ({
          ...prev,
          teachingLabRelationship: {
            ...prev.teachingLabRelationship,
            [relationshipField]: checked
          }
        }));
      } else if (name.startsWith('coachingCapacity.')) {
        const capacityField = name.split('.')[1];
        setFormData(prev => ({
          ...prev,
          coachingCapacity: {
            ...prev.coachingCapacity,
            [capacityField]: checked
          }
        }));
      }
    } else {
      // Handle text inputs and textareas
      if (name === 'curricula.otherText') {
        setFormData(prev => ({
          ...prev,
          curricula: {
            ...prev.curricula,
            otherText: value
          }
        }));
      } else if (name === 'coachingCapacity.otherText') {
        setFormData(prev => ({
          ...prev,
          coachingCapacity: {
            ...prev.coachingCapacity,
            otherText: value
          }
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
    }, 1500);
  };
  useEffect(() => {
    if (isOpen) {
      // Reset animation state when modal opens
      setAnimationComplete(false);
      // Mark animation as complete after transition ends
      const timer = setTimeout(() => {
        setAnimationComplete(true);
      }, 400); // Match this with the CSS transition duration
      return () => clearTimeout(timer);
    }
  }, [isOpen]);
  if (!isOpen) return null;
  // Calculate transform origin based on button position
  const getTransformOriginStyle = () => {
    if (!origin) return {};
    // Convert absolute position to percentage of viewport
    const originX = origin.x / window.innerWidth * 100;
    const originY = origin.y / window.innerHeight * 100;
    return {
      transformOrigin: `${originX}% ${originY}%`
    };
  };
  return <div className={`fixed inset-0 z-50 overflow-y-auto bg-white transition-all duration-400 ease-out ${animationComplete ? '' : 'scale-enter'}`} style={{
    ...getTransformOriginStyle()
  }} data-id="element-192" data-mp="hej0uj4cw48m">
      <style jsx global data-id="element-193" data-mp="26vmzuasm1xdn">{`
        .scale-enter {
          transform: scale(0.1);
          opacity: 0;
        }
        @keyframes rippleEffect {
          0% {
            box-shadow: 0 0 0 0 rgba(0, 122, 143, 0.3);
          }
          100% {
            box-shadow: 0 0 0 100vmax rgba(0, 122, 143, 0);
          }
        }
      `}</style>
      <div className="absolute top-4 right-4 z-50" data-id="element-194" data-mp="2bnoa5r67xe11">
        <button onClick={onClose} className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-center" aria-label="Close modal" data-id="element-195" data-mp="1nwso7aog1d1p">
          <XIcon className="w-6 h-6 text-gray-600" data-id="element-196" data-mp="1iziqz8utbx3b" />
        </button>
      </div>
      <main className="relative min-h-screen bg-gradient-to-r from-white via-primary-50/40 to-blue-50/30 overflow-hidden" data-id="element-197" data-mp="1xvqae6yecuhj">
        {/* Background gradient blobs */}
        <div className="absolute -top-40 -right-20 w-[600px] h-[500px] bg-gradient-to-br from-orange-400/20 via-red-400/20 to-yellow-400/20 rounded-[60%_40%_55%_45%] blur-3xl transform rotate-12 animate-float" data-id="element-198" data-mp="399lvrxfw9zh7"></div>
        <div className="absolute -bottom-20 -left-20 w-[500px] h-[600px] bg-gradient-to-tr from-purple-500/20 via-violet-400/20 to-fuchsia-400/20 rounded-[40%_60%_45%_55%] blur-3xl transform -rotate-12 animate-float" style={{
        animationDelay: '2s'
      }} data-id="element-199" data-mp="3bd769m9wox0p"></div>
        <div className="absolute inset-0 bg-grid-gray-900/[0.02] z-0" data-id="element-200" data-mp="3qb7v5wcda8lc" />
        {/* Header section */}
        <section className="w-full pt-12 pb-3 md:pt-16 md:pb-4 relative z-10" data-id="element-201" data-mp="2snzdydjeflp5">
          <div className="container mx-auto px-4 sm:px-6 relative" data-id="element-202" data-mp="2slwjnhb7r6em">
            <div className="max-w-4xl mx-auto" data-id="element-203" data-mp="2xnc5hbl7tgy2">
              <div id="hero-logo" className="mb-4 text-center" data-id="element-204" data-mp="32m6s6omazpng">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jP7ueorRtM9x7giMYpBsys/nisa-capriola-logotype.png" alt="Nisa" className="h-10 md:h-14 w-auto mx-auto" data-id="element-205" data-mp="32ju5xz9iit4l" />
              </div>
              <div className="mb-4" data-id="element-206" data-mp="29v5d0nim1u3x">
                <h1 className="text-2xl md:text-3xl font-serif font-normal text-gray-900 mb-4 text-center pt-5" data-id="element-207" data-mp="2evt04y3pxqsv">
                  Join the Nisa coaching pilot
                </h1>
                <p className="text-base md:text-lg text-gray-700 px-4" data-id="element-208" data-mp="rzmrrbt3wpam">
                  Nisa is a platform for instructional coaches to extend
                  professional learning between coaching sessions. Our AI tools
                  lighten your workload and strengthen coach–teacher
                  relationships.
                </p>
              </div>
            </div>
          </div>
        </section>
        {/* Content section */}
        <section className="w-full py-8 md:py-12 relative z-10" data-id="element-209" data-mp="8au7is878ejd">
          {/* XL Gradient Blobs - Background Layer */}
          <div className="absolute -top-[400px] -right-[300px] w-[1000px] h-[1000px] bg-gradient-to-br from-blue-400/10 via-cyan-400/10 to-teal-400/10 rounded-[55%_45%_60%_40%] blur-3xl animate-float z-0" data-id="element-210" data-mp="4aa8tk7z92v4"></div>
          <div className="absolute -bottom-[300px] -left-[200px] w-[900px] h-[900px] bg-gradient-to-tr from-purple-400/10 via-violet-400/10 to-fuchsia-400/10 rounded-[40%_60%_55%_45%] blur-3xl animate-float z-0" style={{
          animationDelay: '2s'
        }} data-id="element-211" data-mp="3qjcuton4yfpf"></div>
          <div className="absolute top-[10%] left-[30%] w-[800px] h-[800px] bg-gradient-to-r from-amber-400/10 via-orange-400/10 to-pink-400/10 rounded-[45%_55%_40%_60%] blur-3xl animate-float z-0" style={{
          animationDelay: '3.5s'
        }} data-id="element-212" data-mp="1i797q3nyshpm"></div>
          <div className="absolute bottom-[20%] right-[20%] w-[700px] h-[700px] bg-gradient-to-l from-emerald-400/10 via-green-400/10 to-lime-400/10 rounded-[60%_40%_50%_50%] blur-3xl animate-float z-0" style={{
          animationDelay: '1.5s'
        }} data-id="element-213" data-mp="uj80qz6fp9cb"></div>
          <div className="container mx-auto px-4 sm:px-6 relative z-10" data-id="element-214" data-mp="lk4h9u3curxa">
            <div className="max-w-4xl mx-auto" data-id="element-215" data-mp="qi5zydwvcp88">
              <div className="mb-8" data-id="element-216" data-mp="bk3ufy6a1toc">
                {/* Combined content in a single container */}
                <div className="bg-white/90 backdrop-blur-sm rounded-xl py-5 px-9 shadow-sm border border-gray-100" data-id="element-217" data-mp="gj709ku303xd">
                  <h3 className="text-lg md:text-xl font-sans font-medium text-gray-800 mb-3" data-id="element-218" data-mp="3t29efiwf7aww">
                    Now recruiting for the first coach cohort
                  </h3>
                  <p className="text-sm text-gray-700 mb-6" data-id="element-219" data-mp="6vkzymt4p2r1">
                    We're building a group of US-based instructional coaches,
                    currently working in K-12 schools. Coaches can join at any
                    point in the school year.
                  </p>
                  <div className="border-none border-gray-100 pt-4 mt-4" data-id="element-220" data-mp="ftwlwbj1vcd5">
                    <h3 className="text-lg md:text-xl font-sans font-medium text-gray-800 mb-3" data-id="element-221" data-mp="bu5xufkwracb">
                      Benefits for pilot coaches
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6" data-id="element-222" data-mp="kmpsz61zqwse">
                      <div className="bg-blue-50/70 rounded-lg p-3 border border-blue-100/50 hover:shadow-sm transition-shadow" data-id="element-223" data-mp="zjqj554jajor">
                        <div className="flex items-center" data-id="element-224" data-mp="umgkqzz62xos">
                          <div className="bg-blue-100 rounded-full p-1.5 mr-3 flex-shrink-0" data-id="element-225" data-mp="2mde56ugzeg5b">
                            <CalendarClock className="w-4 h-4 text-blue-700" data-id="element-226" data-mp="2hn7hcwuamzo4" />
                          </div>
                          <span className="text-sm text-gray-800" data-id="element-227" data-mp="2cncq2s6lw82j">
                            Get early access at no cost for '25–'26
                          </span>
                        </div>
                      </div>
                      <div className="bg-cyan-50/70 rounded-lg p-3 border border-cyan-100/50 hover:shadow-sm transition-shadow" data-id="element-228" data-mp="35bgyvmkh519g">
                        <div className="flex items-center" data-id="element-229" data-mp="30e8hho75h199">
                          <div className="bg-cyan-100 rounded-full p-1.5 mr-3 flex-shrink-0" data-id="element-230" data-mp="2wasjoojcguuz">
                            <DollarSign className="w-4 h-4 text-cyan-700" data-id="element-231" data-mp="2rdjysg6tacq1" />
                          </div>
                          <span className="text-sm text-gray-800" data-id="element-232" data-mp="1kxq1jp1ljx8p">
                            Access paid feedback opportunities
                          </span>
                        </div>
                      </div>
                      <div className="bg-primary-50/70 rounded-lg p-3 border border-primary-100/50 hover:shadow-sm transition-shadow" data-id="element-233" data-mp="3rmidgyz28xmi">
                        <div className="flex items-center" data-id="element-234" data-mp="4g990xkxi03l">
                          <div className="bg-primary-100 rounded-full p-1.5 mr-3 flex-shrink-0" data-id="element-235" data-mp="4eoev92j0j69">
                            <Users className="w-4 h-4 text-primary-700" data-id="element-236" data-mp="98iv22ixag0l" />
                          </div>
                          <span className="text-sm text-gray-800" data-id="element-237" data-mp="e5j3ul75pwve">
                            Receive support and connect with other coaches
                          </span>
                        </div>
                      </div>
                      <div className="bg-purple-50/70 rounded-lg p-3 border border-purple-100/50 hover:shadow-sm transition-shadow" data-id="element-238" data-mp="s5tsriowp1dx">
                        <div className="flex items-center" data-id="element-239" data-mp="2pwd9dd9irsgk">
                          <div className="bg-purple-100 rounded-full p-1.5 mr-3 flex-shrink-0" data-id="element-240" data-mp="2pxy3j1rx99dw">
                            <Target className="w-4 h-4 text-purple-700" data-id="element-241" data-mp="2tz03jktnxpgx" />
                          </div>
                          <span className="text-sm text-gray-800" data-id="element-242" data-mp="3s8zogk4bbr03">
                            Shape the future of coaching tools
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Application Form Section */}
              <div className="mb-12" data-id="element-243" data-mp="k09hc9gxcf08">
                <div className="bg-white/90 backdrop-blur-sm rounded-xl p-6 md:p-8 shadow-sm border border-gray-100" data-id="element-244" data-mp="jz7w5c672c4s">
                  {isSubmitted ? <div className="py-8" data-id="element-245" data-mp="trndq8dkwyp5">
                      <div className="flex items-center justify-center w-20 h-20 rounded-full bg-green-100 text-green-700 mb-6 mx-auto" data-id="element-246" data-mp="tqkl9w0lyo74">
                        <CheckCircle2 className="w-10 h-10" data-id="element-247" data-mp="12rskcgay7t29" />
                      </div>
                      <h3 className="text-2xl font-serif font-normal text-gray-900 mb-4 text-center" data-id="element-248" data-mp="xsz8esbb8a47">
                        Thank you for applying!
                      </h3>
                      <p className="text-lg text-gray-700 text-center" data-id="element-249" data-mp="1hcoa2tzaj5wf">
                        We've received your application for the Nisa coaching
                        pilot. We'll be in touch with you soon about next steps.
                      </p>
                    </div> : <>
                      <h3 className="text-xl md:text-xl font-sans font-medium text-gray-800 mb-4" data-id="element-250" data-mp="1cduwl1k07ew9">
                        Tell us about yourself
                      </h3>
                      <p className="text-base text-gray-700 mb-6" data-id="element-251" data-mp="13g1askjv83nc">
                        Please complete the form below. We'll review your
                        application promptly and be in touch soon. If you have
                        any questions, feel free to contact{' '}
                        <a href="mailto:info@nisa.coach" className="text-primary-700 hover:text-primary-800 font-medium" data-id="element-252" data-mp="fp5f3ccagoat">
                          info@nisa.coach
                        </a>
                      </p>
                      <form onSubmit={handleSubmit} className="space-y-6" data-id="element-253" data-mp="cmhdznpebvqe">
                        {/* Full Name Field */}
                        <div className="form-group" data-id="element-254" data-mp="7phgi16m41aw">
                          <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-255" data-mp="2x7lw7ujyd2a">
                            Full Name *
                          </label>
                          <input type="text" id="fullName" name="fullName" value={formData.fullName} onChange={handleInputChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500" data-id="element-256" data-mp="3q2ffa4ro4o3h" />
                        </div>
                        {/* Email Address Field */}
                        <div className="form-group" data-id="element-257" data-mp="3c48y60axv2qi">
                          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-258" data-mp="1ed5xojh47d77">
                            Your Email (Google accounts only) *
                          </label>
                          <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500" placeholder="yourname@gmail.com" data-id="element-259" data-mp="2s9fosp7o2i4f" />
                          <p className="text-xs text-gray-600 mt-1" data-id="element-260" data-mp="321d2c2m5dm6b">
                            Pilot accounts require login via Google email.
                          </p>
                        </div>
                        {/* Location Field */}
                        <div className="form-group" data-id="element-261" data-mp="2eag0jio2g58s">
                          <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-262" data-mp="2eb998p6l54tk">
                            Where are you based?
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-263" data-mp="2n804izdbcfn3">
                            <div data-id="element-264" data-mp="3tpx0cyxqxwkr">
                              <input type="text" id="city" name="city" placeholder="City" value={formData.city} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500" data-id="element-265" data-mp="2jvk8byxccs9" />
                            </div>
                            <div data-id="element-266" data-mp="1unrgqjf9cp55">
                              <input type="text" id="state" name="state" placeholder="State" value={formData.state} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500" data-id="element-267" data-mp="28lg3jwsvy6cu" />
                            </div>
                          </div>
                        </div>
                        {/* Schools Field */}
                        <div className="form-group" data-id="element-268" data-mp="1pmtyk6u8zv3n">
                          <label htmlFor="schools" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-269" data-mp="1poevg341bq4o">
                            Which school(s) do you currently coach?
                          </label>
                          <input type="text" id="schools" name="schools" value={formData.schools} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500" data-id="element-270" data-mp="x3ohzx0ey5vp" />
                        </div>
                        {/* Coaching Capacity Field */}
                        <div className="form-group" data-id="element-271" data-mp="2uu5251l9y2fh">
                          <label htmlFor="coachingCapacity" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-272" data-mp="2uvg4cwz2n24h">
                            How do you serve as a coach at these schools?
                          </label>
                          <div className="space-y-2 mt-2" data-id="element-273" data-mp="3rjvuau63fdi4">
                            <div className="flex items-start" data-id="element-274" data-mp="3rjcdxhw3cx67">
                              <input type="checkbox" id="schoolCoach" name="coachingCapacity.schoolCoach" checked={formData.coachingCapacity.schoolCoach} onChange={handleInputChange} className="mt-1 mr-2" data-id="element-275" data-mp="3dkmis7su17kh" />
                              <label htmlFor="schoolCoach" className="text-sm text-gray-700" data-id="element-276" data-mp="224oa451kgnjs">
                                School Coach
                              </label>
                            </div>
                            <div className="flex items-start" data-id="element-277" data-mp="2xv5nt3erp8d9">
                              <input type="checkbox" id="districtCoach" name="coachingCapacity.districtCoach" checked={formData.coachingCapacity.districtCoach} onChange={handleInputChange} className="mt-1 mr-2" data-id="element-278" data-mp="2tsr28bjpruia" />
                              <label htmlFor="districtCoach" className="text-sm text-gray-700" data-id="element-279" data-mp="2juuqpimv6xjn">
                                District Coach
                              </label>
                            </div>
                            <div className="flex items-start" data-id="element-280" data-mp="3ck1p8hmbjn4w">
                              <input type="checkbox" id="coachingProviderEmployee" name="coachingCapacity.coachingProviderEmployee" checked={formData.coachingCapacity.coachingProviderEmployee} onChange={handleInputChange} className="mt-1 mr-2" data-id="element-281" data-mp="37sa8eysysvry" />
                              <label htmlFor="coachingProviderEmployee" className="text-sm text-gray-700" data-id="element-282" data-mp="43ysh6wqvivc">
                                Coaching Provider Employee
                              </label>
                            </div>
                            <div className="flex items-start" data-id="element-283" data-mp="6pziwxca39i3">
                              <input type="checkbox" id="contractor" name="coachingCapacity.contractor" checked={formData.coachingCapacity.contractor} onChange={handleInputChange} className="mt-1 mr-2" data-id="element-284" data-mp="bnhk21k20kfj" />
                              <label htmlFor="contractor" className="text-sm text-gray-700" data-id="element-285" data-mp="lmgv1qsaks5p">
                                Contractor
                              </label>
                            </div>
                            <div className="flex items-start" data-id="element-286" data-mp="ujr5j9xacd9k">
                              <input type="checkbox" id="administrator" name="coachingCapacity.administrator" checked={formData.coachingCapacity.administrator} onChange={handleInputChange} className="mt-1 mr-2" data-id="element-287" data-mp="zc0sch33sx7m" />
                              <label htmlFor="administrator" className="text-sm text-gray-700" data-id="element-288" data-mp="19bhyt0q6jf4n">
                                Administrator
                              </label>
                            </div>
                            <div className="flex items-start" data-id="element-289" data-mp="2ctqebrmw2jfn">
                              <input type="checkbox" id="otherCapacity" name="coachingCapacity.other" checked={formData.coachingCapacity.other} onChange={handleInputChange} className="mt-1 mr-2" data-id="element-290" data-mp="2hqqoadmv06c1" />
                              <label htmlFor="otherCapacity" className="text-sm text-gray-700" data-id="element-291" data-mp="2qsge5f13ex7p">
                                Other
                              </label>
                              {formData.coachingCapacity.other && <input type="text" id="otherCapacityText" name="coachingCapacity.otherText" value={formData.coachingCapacity.otherText} onChange={handleInputChange} placeholder="Please specify" className="ml-2 px-3 py-1 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500" data-id="element-292" data-mp="30pclvij63fb7" />}
                            </div>
                          </div>
                        </div>
                        {/* Subjects and Grades Field */}
                        <div className="form-group" data-id="element-293" data-mp="17t8zzu4fyyn8">
                          <label htmlFor="subjectsAndGrades" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-294" data-mp="17utvblykylm5">
                            What subjects and grade levels do you coach?
                          </label>
                          <input type="text" id="subjectsAndGrades" name="subjectsAndGrades" value={formData.subjectsAndGrades} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500" data-id="element-295" data-mp="yuop3f54ht9k" />
                        </div>
                        {/* Curriculum Field */}
                        <div className="form-group" data-id="element-296" data-mp="1hqn6p20kc92c">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-297" data-mp="1hp3m4z7vjbur">
                            Which curriculum do you support teachers with?
                          </label>
                          <div className="space-y-2 mt-2" data-id="element-298" data-mp="2a9kd3rpnt72s">
                            <div className="flex items-start" data-id="element-299" data-mp="25g92rgln2864">
                              <input type="checkbox" id="illustrativeMath" name="curricula.illustrativeMath" checked={formData.curricula.illustrativeMath} onChange={handleInputChange} className="mt-1 mr-2" data-id="element-300" data-mp="2ocrdztubyfdc" />
                              <label htmlFor="illustrativeMath" className="text-sm text-gray-700" data-id="element-301" data-mp="2flhab9plh2ux">
                                Illustrative Mathematics
                              </label>
                            </div>
                            <div className="flex items-start" data-id="element-302" data-mp="25m0ava0xrl7e">
                              <input type="checkbox" id="eurekaMath" name="curricula.eurekaMath" checked={formData.curricula.eurekaMath} onChange={handleInputChange} className="mt-1 mr-2" data-id="element-303" data-mp="2ame77ut6d6pl" />
                              <label htmlFor="eurekaMath" className="text-sm text-gray-700" data-id="element-304" data-mp="20nf750mh6je7">
                                Eureka Math
                              </label>
                            </div>
                            <div className="flex items-start" data-id="element-305" data-mp="3v282qzncza65">
                              <input type="checkbox" id="otherCurricula" name="curricula.other" checked={formData.curricula.other} onChange={handleInputChange} className="mt-1 mr-2" data-id="element-306" data-mp="3uzlsgs932gk8" />
                              <label htmlFor="otherCurricula" className="text-sm text-gray-700" data-id="element-307" data-mp="1fem1mkew8lot">
                                Other
                              </label>
                            </div>
                            {formData.curricula.other && <div className="ml-6" data-id="element-308" data-mp="232c1lujc60wn">
                                <input type="text" id="otherCurriculaText" name="curricula.otherText" value={formData.curricula.otherText} onChange={handleInputChange} placeholder="Please specify" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500" data-id="element-309" data-mp="230sg9pitozo0" />
                              </div>}
                          </div>
                        </div>
                        {/* Teaching Lab Relationship Field */}
                        <div className="form-group" data-id="element-310" data-mp="cqpkfqfpy7m9">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-311" data-mp="cs0pruotbnfh">
                            What is your relationship with Teaching Lab? *
                          </label>
                          <p className="text-xs text-gray-700 mb-2" data-id="element-312" data-mp="j8940dhv8vy7">
                            Select all that apply. No affiliation is required to
                            join our pilot.
                          </p>
                          <div className="space-y-2 mt-2" data-id="element-313" data-mp="3rm9e9e5hxp7s">
                            <div className="flex items-start" data-id="element-314" data-mp="3rnsux5v2e2a3">
                              <input type="checkbox" id="fullTimeEmployee" name="teachingLabRelationship.fullTimeEmployee" checked={formData.teachingLabRelationship.fullTimeEmployee} onChange={handleInputChange} className="mt-1 mr-2" data-id="element-315" data-mp="ewtyq9bgvnn" />
                              <label htmlFor="fullTimeEmployee" className="text-sm text-gray-700" data-id="element-316" data-mp="a6swcdifr7xo">
                                Full-time Teaching Lab employee (including
                                coaches)
                              </label>
                            </div>
                            <div className="flex items-start" data-id="element-317" data-mp="36ajyca4vrad2">
                              <input type="checkbox" id="partTimeFacilitator" name="teachingLabRelationship.partTimeFacilitator" checked={formData.teachingLabRelationship.partTimeFacilitator} onChange={handleInputChange} className="mt-1 mr-2" data-id="element-318" data-mp="31bfmtcuhh0jk" />
                              <label htmlFor="partTimeFacilitator" className="text-sm text-gray-700" data-id="element-319" data-mp="2rdjib3w1x3uf">
                                Part-time Teaching Lab facilitator and/or coach
                              </label>
                            </div>
                            <div className="flex items-start" data-id="element-320" data-mp="2dlv2oqmlfx8p">
                              <input type="checkbox" id="tabMember" name="teachingLabRelationship.tabMember" checked={formData.teachingLabRelationship.tabMember} onChange={handleInputChange} className="mt-1 mr-2" data-id="element-321" data-mp="2dk1wtdjt1h1r" />
                              <label htmlFor="tabMember" className="text-sm text-gray-700" data-id="element-322" data-mp="1zmlt49sw81dp">
                                Teacher Advisory Board (TAB) member
                              </label>
                            </div>
                            <div className="flex items-start" data-id="element-323" data-mp="1u1nw73xvxewy">
                              <input type="checkbox" id="notAffiliated" name="teachingLabRelationship.notAffiliated" checked={formData.teachingLabRelationship.notAffiliated} onChange={handleInputChange} className="mt-1 mr-2" data-id="element-324" data-mp="1g0tnslau7dya" />
                              <label htmlFor="notAffiliated" className="text-sm text-gray-700" data-id="element-325" data-mp="28p90302l3i5x">
                                Not affiliated with Teaching Lab
                              </label>
                            </div>
                          </div>
                        </div>
                        {/* How did you discover Nisa Field */}
                        <div className="form-group" data-id="element-326" data-mp="23wfu68vdq3s9">
                          <label htmlFor="hearAboutNisa" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-327" data-mp="2rnbrfliltr6w">
                            How did you discover Nisa?
                          </label>
                          <input type="text" id="hearAboutNisa" name="hearAboutNisa" value={formData.hearAboutNisa} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500" data-id="element-328" data-mp="35skivesfhpa5" />
                        </div>
                        {/* Why interested Field */}
                        <div className="form-group" data-id="element-329" data-mp="3amy6gv0ums49">
                          <label htmlFor="whyInterested" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-330" data-mp="2mw2h04jv8906">
                            Why are you interested in joining our pilot program?
                          </label>
                          <div className="relative" data-id="element-331" data-mp="2wui7c5b2je8m">
                            <textarea id="whyInterested" name="whyInterested" value={formData.whyInterested} onChange={handleInputChange} rows={4} maxLength={500} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 pb-8" data-id="element-332" data-mp="2rtuk7upynhjo"></textarea>
                            <div className="absolute bottom-2 right-3 text-xs text-gray-600" data-id="element-333" data-mp="21kn35mhkjrri">
                              {formData.whyInterested.length}/500
                            </div>
                          </div>
                        </div>
                        {/* Submit Button */}
                        <div className="pt-2 flex justify-center" data-id="element-334" data-mp="2fc1pa26rw92s">
                          <Button type="submit" size="lg" fullWidth disabled={isSubmitting} data-id="element-335" data-mp="2fdct2207xgtw">
                            {isSubmitting ? <span className="flex items-center justify-center" data-id="element-336" data-mp="2p9o3snk5tym8">
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-337" data-mp="2pbsg9tw4k5y4">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-338" data-mp="2y92vfqc2cf8b"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-339" data-mp="yzcx124hubbx"></path>
                                </svg>
                                Submitting...
                              </span> : <>
                                Submit Your Application
                                <ArrowRight className="w-5 h-5 ml-2" data-id="element-340" data-mp="mnaf09xeek5i" />
                              </>}
                          </Button>
                        </div>
                        {/* Form Footer */}
                        <p className="text-xs text-gray-700 mt-4 text-center" data-id="element-341" data-mp="1f9loyb6w9xez">
                          By submitting this form, you agree to be contacted
                          about the Nisa coaching pilot.
                          <br data-id="element-342" data-mp="11bx6fa0av2d3" />
                          We respect your privacy. Your info will never be
                          shared with third parties.
                        </p>
                      </form>
                    </>}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>;
};